/**
 * Globalny konsument Redux (state.ui.notification) → snackbar.
 * Różnica od useNotifications: useNotifications to imperatywne API (showSuccessMessage itd.),
 * ten hook reaguje na dispatch(setNotification(...)) z dowolnego miejsca w aplikacji.
 * Wywołać tylko raz w drzewie (Routes.tsx), żeby uniknąć podwójnego wyświetlania.
 */
import { createElement, ReactNode, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { reduxActions, useDispatch, useSelector } from 'store';

interface ParsedLine {
  objectName: string;
  message: string;
  count: number;
}

interface ParsedNotification {
  title: string;
  lines: ParsedLine[];
}

/**
 * Parsuje komunikat z BE (NotificationResponseDTOConverterService::prepareResponseMessage).
 * Spodziewana struktura:
 *   {tytuł} <div class="notification-lists"> <div class="notification-line">
 *     <span class="notification-object-name">…</span>
 *     <span class="notification-message">…</span>
 *   </div> … </div>
 *
 * Tolerancyjny na warianty (literówka "notfication-lists", brak wrappera linii,
 * tylko span message bez object-name).
 */
const parseNotification = (raw: string): ParsedNotification => {
  const container = document.createElement('div');
  container.innerHTML = raw;

  const lineNodes = container.querySelectorAll('.notification-line');

  // Fallback: bez wrappera linii, ale są same spany
  if (lineNodes.length === 0) {
    const objectSpan = container.querySelector('.notification-object-name');
    const messageSpan = container.querySelector('.notification-message');
    if (!objectSpan && !messageSpan) {
      return { title: container.textContent?.trim() ?? raw.trim(), lines: [] };
    }
    const titleClone = container.cloneNode(true) as HTMLElement;
    titleClone
      .querySelectorAll('.notification-object-name, .notification-message, .notfication-lists, .notification-lists')
      .forEach((n) => n.remove());
    return {
      title: (titleClone.textContent ?? '').trim(),
      lines: [
        {
          objectName: objectSpan?.textContent?.trim() ?? '',
          message: messageSpan?.textContent?.trim() ?? '',
          count: 1
        }
      ]
    };
  }

  // Tytuł = wszystko poza wrapperem list
  const titleClone = container.cloneNode(true) as HTMLElement;
  titleClone
    .querySelectorAll('.notfication-lists, .notification-lists, .notification-line')
    .forEach((n) => n.remove());
  const title = (titleClone.textContent ?? '').trim();

  // Deduplikacja po (objectName + message) z licznikiem
  const seen = new Map<string, ParsedLine>();
  Array.from(lineNodes).forEach((node) => {
    const objectName = node.querySelector('.notification-object-name')?.textContent?.trim() ?? '';
    const message = node.querySelector('.notification-message')?.textContent?.trim() ?? '';
    const key = `${objectName}|||${message}`;
    const existing = seen.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      seen.set(key, { objectName, message, count: 1 });
    }
  });

  return { title, lines: Array.from(seen.values()) };
};

const styles = {
  body: {
    display: 'block',
    width: '100%',
    lineHeight: 1.45,
    fontFamily: 'inherit'
  } as const,
  title: {
    display: 'block',
    fontWeight: 600,
    fontSize: 14,
    marginBottom: 12
  } as const,
  list: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8
  } as const,
  line: {
    display: 'block',
    padding: '10px 12px',
    borderRadius: 6,
    backgroundColor: '#fff',
    color: '#212121',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.12)'
  } as const,
  objectRow: {
    display: 'flex',
    alignItems: 'flex-start' as const,
    gap: 8,
    marginBottom: 4
  } as const,
  objectName: {
    flex: 1,
    fontWeight: 600,
    fontSize: 13,
    color: '#212121',
    wordBreak: 'break-word' as const,
    whiteSpace: 'normal' as const
  } as const,
  badge: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 26,
    height: 20,
    padding: '0 6px',
    borderRadius: 10,
    backgroundColor: 'transparent',
    border: '1px solid rgba(0, 0, 0, 0.2)',
    color: '#5f5f5f',
    fontWeight: 700,
    fontSize: 11,
    lineHeight: 1
  } as const,
  message: {
    display: 'block',
    fontSize: 12.5,
    color: '#5f5f5f'
  } as const
};

const renderNotification = (raw: string): ReactNode => {
  const { title, lines } = parseNotification(raw);

  // Prosty komunikat bez wierszy szczegółów — zwracamy goły string,
  // notistack sam ogarnie układ i nie tworzy zbędnego marginu pod tytułem.
  if (lines.length === 0) {
    return title;
  }

  return createElement(
    'span',
    { style: styles.body },
    title ? createElement('span', { key: 'title', style: styles.title }, title) : null,
    lines.length > 0
      ? createElement(
          'span',
          { key: 'list', style: styles.list },
          ...lines.map((line, idx) =>
            createElement(
              'span',
              { key: `line-${idx}`, style: styles.line },
              line.objectName
                ? createElement(
                    'span',
                    { key: 'obj-row', style: styles.objectRow },
                    createElement('span', { key: 'obj', style: styles.objectName }, line.objectName),
                    line.count > 1
                      ? createElement(
                          'span',
                          { key: 'badge', style: styles.badge },
                          `×${line.count}`
                        )
                      : null
                  )
                : null,
              line.message
                ? createElement('span', { key: 'msg', style: styles.message }, line.message)
                : null
            )
          )
        )
      : null
  );
};

export const useNotificationToSnackbar = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const notification = useSelector((state) => state.ui.notification);

  useEffect(() => {
    if (!notification) {
      dispatch(reduxActions.setNotification(null));
      return;
    }
    const isLongMessage = notification.message.length > 1000;
    enqueueSnackbar(renderNotification(notification.message), {
      variant: notification.variant,
      ...(isLongMessage
        ? { persist: true }
        : notification.variant === 'error'
          ? { autoHideDuration: 6000 }
          : {})
    });
    dispatch(reduxActions.setNotification(null));
  }, [notification, dispatch, enqueueSnackbar]);
};
